import React, { useState } from 'react';
import { Table as AntdTable, Button as AntdButton, Space } from 'antd';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatColumns, searchDataSource } from './TableDataType';
import { TableLoading } from './TableLoading';
import { TableEmpty } from './TableEmpty';
import { TableHeading } from './TableHeading';
import { getTranslatedString } from 'helpers/i18n';
import { TableSearch } from './TableSearch';
import { useWindowSize, sizes } from 'helpers/MediaHelper';
import { RedoOutlined, PlusOutlined } from '@ant-design/icons';

import { useDebounce } from 'use-debounce';

const DEBOUNCE_TIME_IN_MS = 500;

const StyledActionsDiv = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StyledFixedDiv = styled.div`
  padding: 16px;
`;

const Table = ({
  dataSource,
  columns,
  loading,
  title,
  actionButtonLabel,
  actionButtonOnClick,
  showActionButton = false,
  resetButtonOnClick,
  searchTermOnChange,
  onChange,
  pagination = { hideOnSinglePage: true },
  disableInternalSearch,
  scroll,
  expandedRowRender,
  rowExpandable,
  hideActionAndSearch,
  rowSelection,
  size,
  className,
  actionAndSearchStyle,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, DEBOUNCE_TIME_IN_MS);

  const dataSourceToDisplay = disableInternalSearch
    ? dataSource
    : searchDataSource(dataSource, debouncedSearchValue, columns);

  const [width] = useWindowSize();
  const isMobile = width <= sizes.mobile;

  let args = { size, ...props };

  if (typeof pagination === 'object') {
    pagination = { hideOnSinglePage: true, ...pagination };
  }

  if (typeof pagination === 'object' || pagination === false) {
    args = { ...args, pagination };
  }

  if (rowExpandable) {
    if (!expandedRowRender) {
      throw new Error('Expected expandedRowRender prop if rowExpandable provided');
    }
    args = {
      ...args,
      expandable: {
        ...args.expandable,
        rowExpandable: rowExpandable,
        expandedRowRender: expandedRowRender,
      },
    };
  }

  if (loading) {
    // Overrides default table body renderer with custom loading component
    const loadingArgs = {
      components: {
        body: { wrapper: () => <TableLoading colSpan={columns.length + (rowSelection ? 1 : 0)} /> },
      },
      pagination: false,
    };

    args = { ...args, ...loadingArgs };
  }

  if (!dataSourceToDisplay?.length && !loading) {
    // No results found or No data to Show message
    const message = debouncedSearchValue.length
      ? `${getTranslatedString('tablesv4.noResultsFor')} "${debouncedSearchValue}"`
      : getTranslatedString('tablesv4.noDataToShow');

    // Overrides default table body renderer with custom 'no data to show' component
    const noResultsArgs = {
      components: {
        body: {
          wrapper: () => (
            <TableEmpty colSpan={columns.length + (rowSelection ? 1 : 0)} message={message} />
          ),
        },
      },
    };

    args = { ...args, ...noResultsArgs };
  }

  if (scroll) {
    args = { ...args, scroll };
  }

  const handleSearchInputChange = (e) => {
    setSearchValue(e.target.value);
    searchTermOnChange && searchTermOnChange(e.target.value);
  };

  const handleResetTableButtonClick = () => {
    setSearchValue('');
    resetButtonOnClick && resetButtonOnClick();
  };

  const RenderActionButton = () =>
    showActionButton ? (
      <AntdButton
        type="primary"
        size={isMobile ? 'large' : 'default'}
        icon={<PlusOutlined />}
        onClick={actionButtonOnClick}
        data-testid="table-action-button"
      >
        {actionButtonLabel}
      </AntdButton>
    ) : null;

  // Search bar, reset button and action button on desktop
  const DesktopActionElements = (
    <StyledActionsDiv>
      <Space size="middle" align="center">
        <TableSearch onChange={handleSearchInputChange} searchTerm={searchValue} />
        <AntdButton
          type="text"
          size="small"
          icon={<RedoOutlined />}
          onClick={handleResetTableButtonClick}
          data-testid="table-reset-button"
        >
          Reset Table
        </AntdButton>
      </Space>
      <RenderActionButton />
    </StyledActionsDiv>
  );

  // Search bar, reset button and action button on mobile
  const MobileActionElements = (
    <>
      <TableSearch onChange={handleSearchInputChange} searchTerm={searchValue} isMobile />
      <StyledActionsDiv>
        <AntdButton
          type="text"
          size="large"
          icon={<RedoOutlined />}
          onClick={handleResetTableButtonClick}
          data-testid="table-reset-button"
        >
          Reset Table
        </AntdButton>
        <RenderActionButton />
      </StyledActionsDiv>
    </>
  );

  return (
    <>
      {!hideActionAndSearch && (
        <StyledFixedDiv style={actionAndSearchStyle}>
          <TableHeading>{title}</TableHeading>
          {isMobile ? MobileActionElements : DesktopActionElements}
        </StyledFixedDiv>
      )}
      <AntdTable
        {...args}
        rowSelection={rowSelection}
        dataSource={dataSourceToDisplay}
        columns={formatColumns(columns)}
        onChange={onChange}
        className={className}
      />
    </>
  );
};

Table.Summary = AntdTable.Summary;

// For more info on typing and examples, please check both resources:
// Table.stories.js
// and the antd 3.x doc: https://3x.ant.design/components/table/#Table
Table.propTypes = {
  dataSource: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  title: PropTypes.string,
  showActionButton: PropTypes.bool,
  actionButtonLabel: PropTypes.string,
  actionButtonOnClick: PropTypes.func,
  resetButtonOnClick: PropTypes.func,
  searchTermOnChange: PropTypes.func,
  onChange: PropTypes.func,
  pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  disableInternalSearch: PropTypes.bool,
  expandedRowRender: PropTypes.func,
  rowExpandable: PropTypes.func,
  rowSelection: PropTypes.object,
  hideActionAndSearch: PropTypes.bool,
  actionAndSearchStyle: PropTypes.any,
};

Table.defaultProps = {
  dataSource: [],
  columns: [],
  showActionButton: false,
  actionButtonLabel: 'Action Button',
  disableInternalSearch: false,
};

export { Table };
